// The exit interview, held by a signatory (normally Xolile), not the employee's
// manager, so the employee can speak freely about them. Offered to every leaver,
// dismissals included; a declined interview is recorded against the date it was
// offered. The answers cannot change final pay, rehire or anything owed.
//
// Someone without interview access sees only whether it happened.
import { useEffect, useState } from 'react'
import { MessagesSquare } from 'lucide-react'
import { useRecordExitInterview } from '../../hooks/backend/offboarding'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { writeErrorMessage } from '../../utils/writeError'
import { choiceLabel, formatDay, type Answer, type CaseDetail } from '../../utils/offboarding'

export function ExitInterviewForm({ detail, onChanged }: { detail: CaseDetail; onChanged: () => void }) {
  const iv = detail.interview
  const questions = detail.questions
  const na = detail.items.find((i) => i.item_no === 25)?.status === 'not_applicable'
  const editable = ['open', 'awaiting_signoff'].includes(detail.summary.status) && detail.access.can_see_interview
  const record = useRecordExitInterview<{ ok: boolean }>()

  const [status, setStatus] = useState<'offered' | 'held' | 'declined'>(iv?.status ?? 'held')
  const [offeredOn, setOfferedOn] = useState(iv?.offered_on ?? '')
  const [heldOn, setHeldOn] = useState(iv?.held_on ?? '')
  const [mode, setMode] = useState<string>(iv?.mode ?? 'face_to_face')
  const [answers, setAnswers] = useState<Record<string, Answer>>(iv?.answers ?? {})

  useEffect(() => {
    setStatus(iv?.status ?? 'held'); setOfferedOn(iv?.offered_on ?? ''); setHeldOn(iv?.held_on ?? '')
    setMode(iv?.mode ?? 'face_to_face'); setAnswers(iv?.answers ?? {})
  }, [iv])

  if (na) return null

  if (!detail.access.can_see_interview) {
    return (
      <section className="rounded-lg border border-border p-3 text-sm">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><MessagesSquare className="h-4 w-4 text-muted-foreground" /> Exit interview</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {iv ? `Recorded as ${iv.status}${iv.held_on ? ` on ${formatDay(iv.held_on)}` : ''}.` : 'Not recorded yet.'} The answers are for the offboarding signatories.
        </p>
      </section>
    )
  }

  const set = (q: number, patch: Answer) => setAnswers((a) => ({ ...a, [String(q)]: { ...a[String(q)], ...patch } }))

  async function save() {
    try {
      await record.trigger({ case_id: detail.summary.id, status, offered_on: offeredOn || null, held_on: status === 'held' ? heldOn || null : null,
        mode: status === 'held' ? mode : null, answers: status === 'held' ? answers : {} }).result
      toast.success('Exit interview recorded')
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-border p-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <MessagesSquare className="h-4 w-4 text-muted-foreground" /> Exit interview
        {iv && <span className="text-xs font-normal text-muted-foreground">recorded as {iv.status}{iv.interviewer ? ` by ${iv.interviewer}` : ''}</span>}
      </h3>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="space-y-1">
          <Label>Outcome</Label>
          <NativeSelect value={status} disabled={!editable} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="held">Held</option>
            <option value="declined">Declined</option>
            <option value="offered">Offered, not yet held</option>
          </NativeSelect>
        </div>
        <div className="space-y-1">
          <Label>Offered on</Label>
          <Input type="date" value={offeredOn} disabled={!editable} onChange={(e) => setOfferedOn(e.target.value)} />
        </div>
        {status === 'held' && (
          <>
            <div className="space-y-1">
              <Label>Held on</Label>
              <Input type="date" value={heldOn} disabled={!editable} onChange={(e) => setHeldOn(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>How</Label>
              <NativeSelect value={mode} disabled={!editable} onChange={(e) => setMode(e.target.value)}>
                <option value="face_to_face">Face to face</option>
                <option value="phone">By phone</option>
              </NativeSelect>
            </div>
          </>
        )}
      </div>

      {status === 'held' && questions && (
        <ol className="space-y-3">
          {questions.map((q) => {
            const a = answers[String(q.q_no)] ?? {}
            return (
              <li key={q.q_no} className="space-y-1.5">
                <p className="text-sm text-foreground">
                  <span className="mr-1 font-semibold tabular-nums">{q.q_no}.</span>{q.prompt}
                  {q.optional && <span className="ml-1 text-xs text-muted-foreground">(optional)</span>}
                </p>
                {(q.kind === 'reason' || q.kind === 'choice') && q.options && (
                  <NativeSelect value={a.choice ?? ''} disabled={!editable} onChange={(e) => set(q.q_no, { choice: e.target.value || undefined })}>
                    <option value="">Choose</option>
                    {q.options.map((o) => <option key={o} value={o}>{choiceLabel(o)}</option>)}
                  </NativeSelect>
                )}
                {q.scored && (
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" disabled={!editable}
                        onClick={() => set(q.q_no, { score: a.score === n ? undefined : n })}
                        className={cn('h-7 w-7 rounded-md border text-xs tabular-nums',
                          a.score === n ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]' : 'border-border bg-background text-foreground')}>
                        {n}
                      </button>
                    ))}
                    <span className="ml-2 text-[11px] text-muted-foreground">1 poor, 5 excellent</span>
                  </div>
                )}
                <Textarea rows={2} value={a.text ?? ''} disabled={!editable} placeholder="In the employee's words"
                  onChange={(e) => set(q.q_no, { text: e.target.value || undefined })} />
              </li>
            )
          })}
        </ol>
      )}

      {editable && (
        <Button size="sm" variant="brand" onClick={() => void save()} disabled={record.loading || !offeredOn}>
          {record.loading && <Spinner className="mr-1 h-3 w-3" />} Save the interview
        </Button>
      )}
    </section>
  )
}
